import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Link } from "react-router-dom";
// react-icons 4.12's root index.d.ts has only a default export; IconType lives in /lib
// (the fi pack itself imports it from '../lib').
import { IconType } from "react-icons/lib";
import {
  FiChevronRight,
  FiCreditCard,
  FiGift,
  FiRepeat,
  FiX,
} from "react-icons/fi";

export interface TopUpOption {
  id: "bank-deposit" | "sell-crypto" | "sell-gift-card";
  title: string;
  description: string;
  to: string;
  Icon: IconType;
}

// Web has no remote crypto flag (unlike expo's selectConfig), so all three
// options are always listed — matching the unconditional "CRYPTO QUICK SELL"
// entry in src/data/navLinks.tsx.
export const TOP_UP_OPTIONS: TopUpOption[] = [
  {
    id: "bank-deposit",
    title: "Fund via Naira Virtual Account",
    description: "Deposit funds instantly using your dedicated virtual account.",
    to: "/app/deposit",
    Icon: FiCreditCard,
  },
  {
    id: "sell-crypto",
    title: "Sell Crypto",
    description: "Convert your crypto to your GCBUYING wallet balance.",
    to: "/app/quick-sell",
    Icon: FiRepeat,
  },
  {
    id: "sell-gift-card",
    title: "Sell Gift Card",
    description:
      "Exchange your gift cards and receive the proceeds in your GCBUYING wallet.",
    to: "/app/giftcard",
    Icon: FiGift,
  },
];

export const TopUpModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative font-dashboard w-full max-w-md transform overflow-hidden rounded-2xl bg-[#161819] px-4 pt-4 pb-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title className="text-xl text-white font-semibold">
                    Top Up
                  </Dialog.Title>
                  <button
                    type="button"
                    aria-label="Close"
                    onClick={onClose}
                    className="text-white/60 hover:text-white box-content p-2 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <FiX className="text-xl" />
                  </button>
                </div>

                <div className="flex flex-col gap-3">
                  {TOP_UP_OPTIONS.map(({ id, title, description, to, Icon }) => (
                    <Link
                      key={id}
                      to={to}
                      onClick={onClose}
                      className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 p-4 transition-colors"
                    >
                      <span className="box-content p-2.5 rounded-full bg-primary/20 text-primary">
                        <Icon className="text-xl" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm text-white font-medium">
                          {title}
                        </span>
                        <span className="block text-xs text-white/50 truncate">
                          {description}
                        </span>
                      </span>
                      <FiChevronRight className="text-white/40 text-lg shrink-0" />
                    </Link>
                  ))}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
